/**
 * Row renderer for an entry inside the interviewer async-select dropdown.
 * Shows avatar + name + email + (optional) timezone clock. Pure presentation —
 * all data transforms live upstream in useInterviewerField.
 */
import { Box, Flex, Text } from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { CheckCircle } from '@kula-ai/icons-core';

interface InterviewerOptionCellArgs {
  label: string;
  description?: string;
  image?: string;
  /** Weekly interview load; shown as "X / Y hrs" when both present, "X hrs" without a limit. */
  workloadHours?: number;
  workloadLimitHours?: number | null;
  /** Renders the kula selected-check indicator (parity with the default
   *  Select option, whose check kula drops once `renderOption` is set). */
  isSelected?: boolean;
}

function formatWorkload(
  hours: number | undefined,
  limit: number | null | undefined
): string {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '';
  const base = `${hours}`.replace(/\.0$/, '');
  if (typeof limit === 'number' && Number.isFinite(limit)) {
    return `${base} / ${limit} hrs`;
  }
  return `${base} hrs`;
}

export function renderInterviewerOptionCell({
  label,
  description,
  image,
  workloadHours,
  workloadLimitHours,
  isSelected,
}: InterviewerOptionCellArgs) {
  const workload = formatWorkload(workloadHours, workloadLimitHours);
  return (
    <Flex align="center" gap="2" w="100%">
      <Avatar src={image} name={label} size="xs" shape="square" />
      <Flex flexDir="column" gap="0" minW={0} flex="1">
        <Text
          fontSize="sm"
          color="secondary-900"
          noOfLines={1}
          fontWeight="medium"
        >
          {label}
        </Text>
        {description && (
          <Text fontSize="xs" color="secondary-500" noOfLines={1}>
            {description}
          </Text>
        )}
      </Flex>
      {workload && (
        <Text fontSize="xs" color="secondary-700" flexShrink={0}>
          {workload}
        </Text>
      )}
      {isSelected && (
        <Box
          as="span"
          flexShrink={0}
          display="inline-flex"
          lineHeight={0}
          color="primary-500"
        >
          <CheckCircle size={18} />
        </Box>
      )}
    </Flex>
  );
}
